import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import { useTranslation } from '../context/TranslationContext';
import Toast from './Toast';
import ProductSearchModal from './ProductSearchModal';
import './SupplierDatabaseModal.css';

interface SupplierDatabaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSupplierData?: any;
  onSupplierAdded: (supplierIndex: number) => void;
  requestId?: string;
  supplierIndex?: number;
}

interface Supplier {
  _id: string;
  name: string;
  email: string;
  phone: string;
  website: string;
  region: string;
  category: string;
  contact_person: string;
  notes: string;
}

interface SupplierProduct {
  _id: string;
  supplier_id: string;
  specific_id: string;
  product_name: string;
  product_brand: string;
  product_global_code: string;
  product_company_code: string;
  product_description: string;
  product_category: string;
  price: number;
  currency: string;
  delivery_time: string;
  minimum_order: string;
  payment_terms: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

const SupplierDatabaseModal: React.FC<SupplierDatabaseModalProps> = ({
  isOpen,
  onClose,
  initialSupplierData,
  onSupplierAdded,
  requestId,
  supplierIndex
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'search' | 'create' | 'product'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Supplier[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    isVisible: boolean;
  }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  // Supplier form data
  const [supplierFormData, setSupplierFormData] = useState({
    name: initialSupplierData?.supplier_name || initialSupplierData?.company_name || initialSupplierData?.name || '',
    email: initialSupplierData?.supplier_email || initialSupplierData?.email || '',
    phone: initialSupplierData?.supplier_phone || initialSupplierData?.phone || '',
    website: initialSupplierData?.supplier_website || initialSupplierData?.website || '',
    region: initialSupplierData?.region || '',
    category: 'AI Analysis Import',
    contact_person: '',
    notes: ''
  });

  // Product form data - pre-fill with OCR data if available
  const [productFormData, setProductFormData] = useState({
    product_name: initialSupplierData?.products?.[0]?.product_name || initialSupplierData?.product_name || '',
    product_brand: initialSupplierData?.products?.[0]?.product_brand || initialSupplierData?.product_brand || '',
    product_global_code: initialSupplierData?.products?.[0]?.product_global_code || initialSupplierData?.product_global_code || '',
    product_company_code: initialSupplierData?.products?.[0]?.product_company_code || initialSupplierData?.product_company_code || '',
    product_description: initialSupplierData?.products?.[0]?.product_description || initialSupplierData?.product_description || '',
    product_category: initialSupplierData?.products?.[0]?.product_category || initialSupplierData?.product_category || 'General',
    price: initialSupplierData?.products?.[0]?.unit_price || initialSupplierData?.products?.[0]?.price || initialSupplierData?.price || initialSupplierData?.unit_price || 0,
    currency: initialSupplierData?.products?.[0]?.currency || initialSupplierData?.currency || 'TRY',
    delivery_time: initialSupplierData?.products?.[0]?.delivery_time || initialSupplierData?.delivery_time || '',
    minimum_order: '',
    payment_terms: initialSupplierData?.products?.[0]?.payment_terms || '',
    certifications: [] as string[],
    additional_specifications: [] as string[],
    custom_specifications: {} as Record<string, string>,
    quantity: initialSupplierData?.products?.[0]?.quantity || 0,
    unit: initialSupplierData?.products?.[0]?.unit || 'piece',
    importance: 'price',
    urgency: 'normal',
    current_price: initialSupplierData?.products?.[0]?.unit_price || 0,
    additional_notes: initialSupplierData?.products?.[0]?.notes || '',
    custom_filters: {} as Record<string, any>
  });

  const { token } = useAuth();

  // Search suppliers
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.SUPPLIER_SEARCH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ query: searchQuery.trim() })
      });

      const result = await response.json();

      if (result.success) {
        setSearchResults(result.data);
      } else {
        setError(result.message || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setError('Failed to search suppliers. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Select existing supplier
  const handleSelectSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    
    if (initialSupplierData) {
      setProductFormData({
        product_name: initialSupplierData?.products?.[0]?.product_name || initialSupplierData.product_name || '',
        product_brand: initialSupplierData?.products?.[0]?.product_brand || initialSupplierData.product_brand || '',
        product_global_code: initialSupplierData?.products?.[0]?.product_global_code || initialSupplierData.product_global_code || '',
        product_company_code: initialSupplierData?.products?.[0]?.product_company_code || initialSupplierData.product_company_code || '',
        product_description: initialSupplierData?.products?.[0]?.product_description || initialSupplierData.product_description || '',
        product_category: initialSupplierData?.products?.[0]?.product_category || initialSupplierData.product_category || 'General',
        price: initialSupplierData?.products?.[0]?.unit_price || initialSupplierData?.products?.[0]?.price || initialSupplierData.price || initialSupplierData.budget || 0,
        currency: initialSupplierData?.products?.[0]?.currency || initialSupplierData.currency || 'TRY',
        delivery_time: initialSupplierData?.products?.[0]?.delivery_time || initialSupplierData.delivery_time || '',
        minimum_order: '',
        payment_terms: initialSupplierData?.products?.[0]?.payment_terms || '',
        certifications: [],
        additional_specifications: initialSupplierData?.products?.[0]?.additional_specifications || initialSupplierData.additional_specifications || [],
        custom_specifications: initialSupplierData?.products?.[0]?.custom_specifications || initialSupplierData.custom_specifications || {},
        quantity: initialSupplierData?.products?.[0]?.quantity || initialSupplierData.quantity || 0,
        unit: initialSupplierData?.products?.[0]?.unit || initialSupplierData.unit || 'piece',
        importance: initialSupplierData?.products?.[0]?.importance || initialSupplierData.importance || 'price',
        urgency: initialSupplierData?.products?.[0]?.urgency || initialSupplierData.urgency || 'normal',
        current_price: initialSupplierData?.products?.[0]?.unit_price || initialSupplierData.current_price || 0,
        additional_notes: initialSupplierData?.products?.[0]?.notes || initialSupplierData.additional_notes || '',
        custom_filters: initialSupplierData?.products?.[0]?.custom_filters || initialSupplierData.custom_filters || {}
      });
    }
    
    setStep('product');
  };

  // Create new supplier (temporary - not saved to database yet)
  const handleCreateSupplier = async () => {
    if (!supplierFormData.name.trim() || !supplierFormData.email.trim()) {
      setError('Supplier name and email are required');
      return;
    }

    setSelectedSupplier({
      _id: 'temp_' + Date.now(),
      name: supplierFormData.name,
      email: supplierFormData.email,
      phone: supplierFormData.phone,
      website: supplierFormData.website,
      region: supplierFormData.region,
      category: supplierFormData.category,
      contact_person: supplierFormData.contact_person,
      notes: supplierFormData.notes
    });
    
    if (initialSupplierData) {
      setProductFormData({
        product_name: initialSupplierData?.products?.[0]?.product_name || initialSupplierData.product_name || '',
        product_brand: initialSupplierData?.products?.[0]?.product_brand || initialSupplierData.product_brand || '',
        product_global_code: initialSupplierData?.products?.[0]?.product_global_code || initialSupplierData.product_global_code || '',
        product_company_code: initialSupplierData?.products?.[0]?.product_company_code || initialSupplierData.product_company_code || '',
        product_description: initialSupplierData?.products?.[0]?.product_description || initialSupplierData.product_description || '',
        product_category: initialSupplierData?.products?.[0]?.product_category || initialSupplierData.product_category || 'General',
        price: initialSupplierData?.products?.[0]?.unit_price || initialSupplierData?.products?.[0]?.price || initialSupplierData.price || initialSupplierData.budget || 0,
        currency: initialSupplierData?.products?.[0]?.currency || initialSupplierData.currency || 'TRY',
        delivery_time: initialSupplierData?.products?.[0]?.delivery_time || initialSupplierData.delivery_time || '',
        minimum_order: '',
        payment_terms: initialSupplierData?.products?.[0]?.payment_terms || '',
        certifications: [],
        additional_specifications: initialSupplierData?.products?.[0]?.additional_specifications || initialSupplierData.additional_specifications || [],
        custom_specifications: initialSupplierData?.products?.[0]?.custom_specifications || initialSupplierData.custom_specifications || {},
        quantity: initialSupplierData?.products?.[0]?.quantity || initialSupplierData.quantity || 0,
        unit: initialSupplierData?.products?.[0]?.unit || initialSupplierData.unit || 'piece',
        importance: initialSupplierData?.products?.[0]?.importance || initialSupplierData.importance || 'price',
        urgency: initialSupplierData?.products?.[0]?.urgency || initialSupplierData.urgency || 'normal',
        current_price: initialSupplierData?.products?.[0]?.unit_price || initialSupplierData.current_price || 0,
        additional_notes: initialSupplierData?.products?.[0]?.notes || initialSupplierData.additional_notes || '',
        custom_filters: initialSupplierData?.products?.[0]?.custom_filters || initialSupplierData.custom_filters || {}
      });
    }
    
    setStep('product');
  };

  // Add product to supplier
  const handleAddProduct = async () => {
    if (!productFormData.product_name.trim()) {
      setError('Product name is required');
      return;
    }

    if (!selectedSupplier) {
      setError('No supplier selected');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      let result;
      
      if (selectedSupplier._id.startsWith('temp_')) {
        if (!requestId || supplierIndex === undefined) {
          setError('Request ID and supplier index are required for new suppliers');
          return;
        }

        // Check if this is OCR data
        const isOcrData = initialSupplierData?.products?.[0]?.product_name;
        
        let endpoint;
        if (isOcrData) {
          // Use OCR-specific endpoint
          endpoint = API_ENDPOINTS.ADD_OCR_TO_DATABASE(requestId);
        } else {
          // Use regular endpoint
          endpoint = API_ENDPOINTS.ADD_SUPPLIER_WITH_PRODUCT(requestId, supplierIndex.toString());
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            supplier: {
              name: selectedSupplier.name,
              email: selectedSupplier.email,
              phone: selectedSupplier.phone,
              website: selectedSupplier.website,
              region: selectedSupplier.region,
              category: selectedSupplier.category,
              contact_person: selectedSupplier.contact_person,
              notes: selectedSupplier.notes
            },
            product: productFormData
          })
        });

        result = await response.json();
      } else {
        const response = await fetch(API_ENDPOINTS.ADD_PRODUCT_TO_SUPPLIER(selectedSupplier._id), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...productFormData,
            request_id: requestId,
            supplier_index: supplierIndex
          })
        });

        result = await response.json();
      }

      if (result.success) {
        setToast({
          message: `✅ Successfully added "${productFormData.product_name}" to ${selectedSupplier.name}! The supplier has been added to the database.`,
          type: 'success',
          isVisible: true
        });
        
        // Wait a bit before closing to show the success message
        setTimeout(() => {
          onSupplierAdded(supplierIndex || 0); // Pass the supplier index
          onClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to add product');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setError('Failed to add product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSupplier(null);
    setError('');
    setSupplierFormData({
      name: initialSupplierData?.supplier_name || '',
      email: initialSupplierData?.supplier_email || '',
      phone: initialSupplierData?.supplier_phone || '',
      website: initialSupplierData?.supplier_website || '',
      region: initialSupplierData?.region || '',
      category: 'AI Analysis Import',
      contact_person: '',
      notes: ''
    });
    setProductFormData({
      product_name: initialSupplierData?.product_name || '',
      product_brand: initialSupplierData?.product_brand || '',
      product_global_code: initialSupplierData?.product_global_code || '',
      product_company_code: initialSupplierData?.product_company_code || '',
      product_description: initialSupplierData?.product_description || '',
      product_category: initialSupplierData?.product_category || 'General',
      price: initialSupplierData?.price || initialSupplierData?.budget || 0,
      currency: initialSupplierData?.currency || 'TRY',
      delivery_time: initialSupplierData?.delivery_time || '',
      minimum_order: '',
      payment_terms: '',
      certifications: [],
      additional_specifications: initialSupplierData?.additional_specifications || [],
      custom_specifications: initialSupplierData?.custom_specifications || {},
      quantity: initialSupplierData?.quantity || 0,
      unit: initialSupplierData?.unit || 'piece',
      importance: initialSupplierData?.importance || 'price',
      urgency: initialSupplierData?.urgency || 'normal',
      current_price: initialSupplierData?.current_price || 0,
      additional_notes: initialSupplierData?.additional_notes || '',
      custom_filters: initialSupplierData?.custom_filters || {}
    });
    onClose();
  };

  // Handle back to search
  const handleBackToSearch = () => {
    setStep('search');
    setSelectedSupplier(null);
    setError('');
    setSupplierFormData({
      name: initialSupplierData?.supplier_name || '',
      email: initialSupplierData?.supplier_email || '',
      phone: initialSupplierData?.supplier_phone || '',
      website: initialSupplierData?.supplier_website || '',
      region: initialSupplierData?.region || '',
      category: 'AI Analysis Import',
      contact_person: '',
      notes: ''
    });
    setProductFormData({
      product_name: initialSupplierData?.product_name || '',
      product_brand: initialSupplierData?.product_brand || '',
      product_global_code: initialSupplierData?.product_global_code || '',
      product_company_code: initialSupplierData?.product_company_code || '',
      product_description: initialSupplierData?.product_description || '',
      product_category: initialSupplierData?.product_category || 'General',
      price: initialSupplierData?.price || initialSupplierData?.budget || 0,
      currency: initialSupplierData?.currency || 'TRY',
      delivery_time: initialSupplierData?.delivery_time || '',
      minimum_order: '',
      payment_terms: '',
      certifications: [],
      additional_specifications: initialSupplierData?.additional_specifications || [],
      custom_specifications: initialSupplierData?.custom_specifications || {},
      quantity: initialSupplierData?.quantity || 0,
      unit: initialSupplierData?.unit || 'piece',
      importance: initialSupplierData?.importance || 'price',
      urgency: initialSupplierData?.urgency || 'normal',
      current_price: initialSupplierData?.current_price || 0,
      additional_notes: initialSupplierData?.additional_notes || '',
      custom_filters: initialSupplierData?.custom_filters || {}
    });
  };

  // Handle product selection from search
  const handleProductSelect = (product: SupplierProduct) => {
    setProductFormData(prev => ({
      ...prev,
      product_name: product.product_name,
      product_brand: product.product_brand,
      product_global_code: product.product_global_code,
      product_category: product.product_category,
      product_description: product.product_description
    }));
    setIsProductSearchOpen(false);
  };

  // Initialize product form data when modal opens
  useEffect(() => {
    if (isOpen && initialSupplierData) {
      setProductFormData({
        product_name: initialSupplierData.product_name || '',
        product_brand: initialSupplierData.product_brand || '',
        product_global_code: initialSupplierData.product_global_code || '',
        product_company_code: initialSupplierData.product_company_code || '',
        product_description: initialSupplierData.product_description || '',
        product_category: initialSupplierData.product_category || 'General',
        price: initialSupplierData.price || initialSupplierData.budget || 0,
        currency: initialSupplierData.currency || 'TRY',
        delivery_time: initialSupplierData.delivery_time || '',
        minimum_order: '',
        payment_terms: '',
        certifications: [],
        additional_specifications: initialSupplierData.additional_specifications || [],
        custom_specifications: initialSupplierData.custom_specifications || {},
        quantity: initialSupplierData.quantity || 0,
        unit: initialSupplierData.unit || 'piece',
        importance: initialSupplierData.importance || 'price',
        urgency: initialSupplierData.urgency || 'normal',
        current_price: initialSupplierData.current_price || 0,
        additional_notes: initialSupplierData.additional_notes || '',
        custom_filters: initialSupplierData.custom_filters || {}
      });
    }
  }, [isOpen, initialSupplierData]);

  // Auto-search when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  if (!isOpen) return null;

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
      <div className="supplier-db-modal-overlay" onClick={handleClose}>
        <div className="supplier-db-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="supplier-db-modal-header">
            <h2>
              {step === 'search' && t('components.supplier_database_modal.search_create_supplier')}
              {step === 'create' && t('common.add_company')}
              {step === 'product' && t('common.add_product_to', { supplier_name: selectedSupplier?.name || '' })}
            </h2>
            <button className="close-btn" onClick={handleClose}>×</button>
          </div>

          <div className="supplier-db-modal-body">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Step 1: Search */}
            {step === 'search' && (
              <div className="search-step">
                <div className="search-section">
                  <h3>{t('components.supplier_database_modal.search_existing_suppliers')}</h3>
                  <div className="search-input-group">
                    <input
                      type="text"
                      placeholder={t('common.search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    {isSearching && <div className="loading-spinner">Searching...</div>}
                  </div>

                  {searchResults.length > 0 && (
                    <div className="search-results">
                      <h4>Found Suppliers:</h4>
                      {searchResults.map((supplier) => (
                        <div
                          key={supplier._id}
                          className="search-result-item"
                          onClick={() => handleSelectSupplier(supplier)}
                        >
                          <div className="supplier-info">
                            <strong>{supplier.name}</strong>
                            <span>{supplier.email}</span>
                            {supplier.phone && <span>📞 {supplier.phone}</span>}
                            {supplier.region && <span>📍 {supplier.region}</span>}
                          </div>
                          <button className="select-btn">Select & Add Product</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {searchQuery && searchResults.length === 0 && !isSearching && (
                    <div className="no-results">
                      <p>No suppliers found. You can create a new one below.</p>
                    </div>
                  )}
                </div>

                <div className="divider">
                  <span>OR</span>
                </div>

                <div className="create-section">
                  <h3>{t('components.supplier_database_modal.create_new_supplier')}</h3>
                  <p>{t('components.supplier_database_modal.create_new_supplier_description')}</p>
                  <button
                    className="create-new-btn"
                    onClick={() => setStep('create')}
                  >
                    {t('components.supplier_database_modal.create_new_supplier')}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Create Supplier */}
            {step === 'create' && (
              <div className="create-step">
                <h3>{t('components.supplier_database_modal.supplier_information')}</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.company_name')} *</label>
                    <input
                      type="text"
                      value={supplierFormData.name}
                      onChange={(e) => setSupplierFormData({...supplierFormData, name: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_company_name')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.email')} *</label>
                    <input
                      type="email"
                      value={supplierFormData.email}
                      onChange={(e) => setSupplierFormData({...supplierFormData, email: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_email_address')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.phone')}</label>
                    <input
                      type="text"
                      value={supplierFormData.phone}
                      onChange={(e) => setSupplierFormData({...supplierFormData, phone: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_phone_number')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.website')}</label>
                    <input
                      type="url"
                      value={supplierFormData.website}
                      onChange={(e) => setSupplierFormData({...supplierFormData, website: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_website_url')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.region')}</label>
                    <select
                      value={supplierFormData.region}
                      onChange={(e) => setSupplierFormData({...supplierFormData, region: e.target.value})}
                    >
                      <option value="">{t('components.supplier_database_modal.select_region')}</option>
                      <option value="CH">Switzerland (CH)</option>
                      <option value="DE">Germany (DE)</option>
                      <option value="TR">Turkey (TR)</option>
                      <option value="DACH">DACH Region</option>
                      <option value="EU">European Union (EU)</option>
                      <option value="US">United States (US)</option>
                      <option value="ASIA">Asia Pacific</option>
                      <option value="GLOBAL">Global</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('components.supplier_database_modal.contact_person')}</label>
                    <input
                      type="text"
                      value={supplierFormData.contact_person}
                      onChange={(e) => setSupplierFormData({...supplierFormData, contact_person: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_contact_person_name')}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>{t('components.supplier_database_modal.notes')}</label>
                    <textarea
                      value={supplierFormData.notes}
                      onChange={(e) => setSupplierFormData({...supplierFormData, notes: e.target.value})}
                      placeholder={t('components.supplier_database_modal.enter_additional_notes')}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Add Product */}
            {step === 'product' && (
              <div className="product-step">
                <h3>Product Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Product Name *</label>
                    <div className="product-input-container">
                      <input
                        type="text"
                        value={productFormData.product_name}
                        onChange={(e) => setProductFormData({...productFormData, product_name: e.target.value})}
                        placeholder="Enter product name"
                      />
                      <button
                        type="button"
                        className="search-product-button"
                        onClick={() => setIsProductSearchOpen(true)}
                        title="Search existing products"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                          <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Search
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Product Brand</label>
                    <input
                      type="text"
                      value={productFormData.product_brand}
                      onChange={(e) => setProductFormData({...productFormData, product_brand: e.target.value})}
                      placeholder="Enter product brand"
                    />
                  </div>
                  <div className="form-group">
                    <label>Global Product Code</label>
                    <input
                      type="text"
                      value={productFormData.product_global_code}
                      onChange={(e) => setProductFormData({...productFormData, product_global_code: e.target.value})}
                      placeholder="Enter global product code"
                    />
                  </div>
                  <div className="form-group">
                    <label>Company Product Code</label>
                    <input
                      type="text"
                      value={productFormData.product_company_code}
                      onChange={(e) => setProductFormData({...productFormData, product_company_code: e.target.value})}
                      placeholder="Enter company product code"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input
                      type="number"
                      value={productFormData.price}
                      onChange={(e) => setProductFormData({...productFormData, price: parseFloat(e.target.value) || 0})}
                      placeholder="Enter price"
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency</label>
                    <select
                      value={productFormData.currency}
                      onChange={(e) => setProductFormData({...productFormData, currency: e.target.value})}
                    >
                      <option value="CHF">CHF</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                      <option value="TRY">TRY</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Delivery Time</label>
                    <input
                      type="text"
                      value={productFormData.delivery_time}
                      onChange={(e) => setProductFormData({...productFormData, delivery_time: e.target.value})}
                      placeholder="e.g., 2-3 weeks"
                    />
                  </div>
                  <div className="form-group">
                    <label>Minimum Order</label>
                    <input
                      type="text"
                      value={productFormData.minimum_order}
                      onChange={(e) => setProductFormData({...productFormData, minimum_order: e.target.value})}
                      placeholder="e.g., 100 pieces"
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Terms</label>
                    <input
                      type="text"
                      value={productFormData.payment_terms}
                      onChange={(e) => setProductFormData({...productFormData, payment_terms: e.target.value})}
                      placeholder="e.g., 30 days net"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Product Description</label>
                    <textarea
                      value={productFormData.product_description}
                      onChange={(e) => setProductFormData({...productFormData, product_description: e.target.value})}
                      placeholder="Enter product description"
                      rows={3}
                    />
                  </div>
                  
                  {/* Additional Product Information */}
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      value={productFormData.quantity}
                      onChange={(e) => setProductFormData({...productFormData, quantity: parseInt(e.target.value) || 0})}
                      placeholder="Enter quantity"
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <select
                      value={productFormData.unit}
                      onChange={(e) => setProductFormData({...productFormData, unit: e.target.value})}
                    >
                      <option value="piece">{t('common.piece')}</option>
                      <option value="kg">{t('common.kilogram')}</option>
                      <option value="m">{t('common.meter')}</option>
                      <option value="liter">{t('common.liter')}</option>
                      <option value="box">{t('common.box')}</option>
                      <option value="set">{t('common.set')}</option>
                      <option value="hour">{t('common.hour')}</option>
                      <option value="day">{t('common.day')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Importance</label>
                    <select
                      value={productFormData.importance}
                      onChange={(e) => setProductFormData({...productFormData, importance: e.target.value})}
                    >
                      <option value="time">Time</option>
                      <option value="price">Price</option>
                      <option value="quality">Quality</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Urgency</label>
                    <select
                      value={productFormData.urgency}
                      onChange={(e) => setProductFormData({...productFormData, urgency: e.target.value})}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Current Price</label>
                    <input
                      type="number"
                      value={productFormData.current_price}
                      onChange={(e) => setProductFormData({...productFormData, current_price: parseFloat(e.target.value) || 0})}
                      placeholder="Enter current price"
                    />
                  </div>
                  
                  {/* Additional Specifications */}
                  <div className="form-group full-width">
                    <label>Additional Specifications</label>
                    <div className="specifications-container">
                      {productFormData.additional_specifications.map((spec, index) => (
                        <div key={index} className="specification-item">
                          <input
                            type="text"
                            value={spec}
                            onChange={(e) => {
                              const newSpecs = [...productFormData.additional_specifications];
                              newSpecs[index] = e.target.value;
                              setProductFormData({...productFormData, additional_specifications: newSpecs});
                            }}
                            placeholder="Enter specification"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newSpecs = productFormData.additional_specifications.filter((_, i) => i !== index);
                              setProductFormData({...productFormData, additional_specifications: newSpecs});
                            }}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setProductFormData({
                            ...productFormData,
                            additional_specifications: [...productFormData.additional_specifications, '']
                          });
                        }}
                        className="add-btn"
                      >
                        + Add Specification
                      </button>
                    </div>
                  </div>
                  
                  {/* Custom Specifications */}
                  <div className="form-group full-width">
                    <label>Custom Specifications</label>
                    <div className="custom-specifications-container">
                      {Object.entries(productFormData.custom_specifications).map(([key, value], index) => (
                        <div key={index} className="custom-specification-item">
                          <input
                            type="text"
                            value={key}
                            onChange={(e) => {
                              const newSpecs = {...productFormData.custom_specifications};
                              delete newSpecs[key];
                              newSpecs[e.target.value] = value;
                              setProductFormData({...productFormData, custom_specifications: newSpecs});
                            }}
                            placeholder="Specification name"
                            className="spec-key"
                          />
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => {
                              const newSpecs = {...productFormData.custom_specifications};
                              newSpecs[key] = e.target.value;
                              setProductFormData({...productFormData, custom_specifications: newSpecs});
                            }}
                            placeholder="Specification value"
                            className="spec-value"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newSpecs = {...productFormData.custom_specifications};
                              delete newSpecs[key];
                              setProductFormData({...productFormData, custom_specifications: newSpecs});
                            }}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newSpecs = {...productFormData.custom_specifications};
                          newSpecs[`spec_${Object.keys(productFormData.custom_specifications).length + 1}`] = '';
                          setProductFormData({...productFormData, custom_specifications: newSpecs});
                        }}
                        className="add-btn"
                      >
                        + Add Custom Specification
                      </button>
                    </div>
                  </div>
                  
                  {/* Additional Notes */}
                  <div className="form-group full-width">
                    <label>Additional Notes</label>
                    <textarea
                      value={productFormData.additional_notes}
                      onChange={(e) => setProductFormData({...productFormData, additional_notes: e.target.value})}
                      placeholder="Enter additional notes"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="supplier-db-modal-footer">
            <button
              className="cancel-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('components.supplier_database_modal.cancel')}
            </button>
            
            {step === 'search' && (
              <button
                className="back-btn"
                onClick={() => setStep('search')}
                disabled={isSubmitting}
              >
                {t('components.supplier_database_modal.back')}
              </button>
            )}
            
            {step === 'create' && (
              <>
                <button
                  className="back-btn"
                  onClick={handleBackToSearch}
                  disabled={isSubmitting}
                >
                  {t('components.supplier_database_modal.back_to_search')}
                </button>
                <button
                  className="continue-btn"
                  onClick={handleCreateSupplier}
                  disabled={isSubmitting || !supplierFormData.name.trim() || !supplierFormData.email.trim()}
                >
                  {isSubmitting ? 'Creating...' : 'Continue to Add Product'}
                </button>
              </>
            )}
            
            {step === 'product' && (
              <>
                <button
                  className="back-btn"
                  onClick={handleBackToSearch}
                  disabled={isSubmitting}
                >
                  {t('components.supplier_database_modal.back_to_search')}
                </button>
                <button
                  className="add-btn"
                  onClick={handleAddProduct}
                  disabled={isSubmitting || !productFormData.product_name.trim()}
                >
                  {isSubmitting ? 'Adding...' : 'Add Supplier & Product'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Product Search Modal */}
      <ProductSearchModal
        isOpen={isProductSearchOpen}
        onClose={() => setIsProductSearchOpen(false)}
        onProductSelect={handleProductSelect}
      />
    </>
  );
};

export default SupplierDatabaseModal; 